export function formatToCr(askStr: string | null | undefined): string {
  if (!askStr) return 'N/A';
  
  const lowerStr = askStr.toLowerCase().trim();
  if (lowerStr === 'not available' || lowerStr === 'not found' || lowerStr === 'n/a') {
    return askStr;
  }

  // Split by range indicators: hyphen, en-dash, or 'to'
  const parts = askStr.split(/\s*(?:-|–|to)\s*/i);
  
  const min = parseToNumber(parts[0]);
  const max = parts.length > 1 ? parseToNumber(parts[1]) : null;

  const formatNumber = (val: number): string => {
    if (val === 0) return '₹0';
    if (val >= 10000000) {
      return `₹${+(val / 10000000).toFixed(2)} Cr`;
    } else if (val >= 100000) {
      return `₹${+(val / 100000).toFixed(2)} Lakhs`;
    } else if (val >= 1000) {
      return `₹${+(val / 1000).toFixed(2)}K`;
    }
    return `₹${val.toLocaleString('en-IN')}`;
  };

  if (min !== null && max !== null) {
    return `${formatNumber(min)} - ${formatNumber(max)}`;
  } else if (min !== null) {
    return formatNumber(min);
  }

  // If we couldn't parse it successfully, return the original string safely
  return askStr;
}

export function formatIndianNumber(value: string | number | null | undefined, fallback = 'N/A'): string {
  if (value === null || value === undefined || value === '') return fallback;

  const numericValue = typeof value === 'number'
    ? value
    : parseToNumber(String(value));

  if (numericValue === null || !Number.isFinite(numericValue)) return fallback;

  return numericValue.toLocaleString('en-IN', {
    maximumFractionDigits: Number.isInteger(numericValue) ? 0 : 2,
  });
}

export function formatIndianCurrency(value: string | number | null | undefined, fallback = 'N/A'): string {
  const formatted = formatIndianNumber(value, fallback);
  return formatted === fallback ? fallback : `₹${formatted}`;
}

export function parseToNumber(valStr: string | null | undefined): number | null {
  if (!valStr) return null;
  // Remove commas, and common currency strings
  let cleanStr = valStr.replace(/,/g, '').replace(/inr|₹|rs\.?|\$/gi, '').trim();
  
  // Match the number and optional magnitude suffix
  // e.g. "25 Lakhs", "1.5 Cr", "2M", "5000"
  const match = cleanStr.match(/([\d.]+)\s*(lakhs?|l|crs?|crores?|m|mn|millions?|k)?/i);
  if (!match) return null;

  const num = parseFloat(match[1]);
  if (isNaN(num)) return null;

  const multiplier = match[2]?.toLowerCase();

  let actualValue = num;
  if (multiplier) {
    if (multiplier.startsWith('l')) {
      actualValue = num * 100000;
    } else if (multiplier.startsWith('cr') || multiplier.startsWith('crore')) {
      actualValue = num * 10000000;
    } else if (multiplier === 'm' || multiplier === 'mn' || multiplier.startsWith('million')) {
      actualValue = num * 1000000;
    } else if (multiplier === 'k') {
      actualValue = num * 1000;
    }
  }

  return actualValue;
}

/**
 * A rupee amount in the narrowest honest form: "₹2.5Cr", "₹45L", "₹8.2K".
 * For number columns tight enough that "₹2,50,00,000" would not fit and
 * `formatToCr`'s " Lakhs" spelling would wrap.
 */
export function formatCompactINR(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  if (abs === 0) return '₹0';
  if (abs >= 10000000) return `${sign}₹${+(abs / 10000000).toFixed(2)}Cr`;
  if (abs >= 100000) return `${sign}₹${+(abs / 100000).toFixed(2)}L`;
  if (abs >= 1000) return `${sign}₹${+(abs / 1000).toFixed(1)}K`;
  return `${sign}₹${Math.round(abs).toLocaleString('en-IN')}`;
}
